<template>
  <div class="master-data-addon">
    <div class="addon-head">
      <div>
        <span>HRM_MASTER_DATA_GOVERNANCE</span>
        <h2>主数据质量治理中心</h2>
        <p>跨员工、部门、角色、文件和参保数据，识别关键字段缺失、孤立关系、重复身份和部门主数据质量风险。</p>
      </div>
      <el-button type="primary" icon="el-icon-refresh" :loading="loading" @click="loadData">刷新</el-button>
    </div>

    <el-row :gutter="16" class="metric-row">
      <el-col v-for="item in metrics" :key="item.label" :xs="24" :sm="12" :lg="6">
        <div class="metric-card" :class="item.tone">
          <span>{{ item.label }}</span>
          <strong>{{ item.value }}</strong>
          <em>{{ item.meta }}</em>
        </div>
      </el-col>
    </el-row>

    <el-row :gutter="16">
      <el-col :xs="24" :lg="14">
        <el-card shadow="never">
          <div slot="header">部门主数据质量排行</div>
          <el-table :data="overview.deptQualityRanking" size="small" v-loading="loading">
            <el-table-column prop="dept_name" label="部门" min-width="130" />
            <el-table-column prop="staff_count" label="员工" width="70" />
            <el-table-column prop="active_staff_count" label="在职" width="70" />
            <el-table-column prop="critical_staff_count" label="严重缺口" width="90" />
            <el-table-column prop="avg_quality_score" label="均分" width="80" />
            <el-table-column prop="quality_warning_count" label="预警" width="80" />
          </el-table>
        </el-card>
      </el-col>
      <el-col :xs="24" :lg="10">
        <el-card shadow="never">
          <div slot="header">字段缺口分布</div>
          <div class="status-list">
            <div v-for="item in overview.fieldGapDistribution" :key="item.gap_name" class="status-item">
              <span>{{ item.gap_name }}</span>
              <strong>{{ item.gap_count }}</strong>
            </div>
          </div>
        </el-card>
      </el-col>
    </el-row>

    <el-row :gutter="16" class="lower-row">
      <el-col :xs="24" :lg="15">
        <el-card shadow="never">
          <div slot="header">高风险主数据清单</div>
          <el-table :data="overview.highRiskMasterDataList" size="small" v-loading="loading">
            <el-table-column prop="code" label="工号" width="100" />
            <el-table-column prop="staff_name" label="姓名" width="110" />
            <el-table-column prop="dept_name" label="部门" min-width="120" />
            <el-table-column prop="phone" label="电话" width="130" />
            <el-table-column prop="issue_count" label="问题数" width="80" />
            <el-table-column prop="quality_score" label="质量分" width="90" />
          </el-table>
        </el-card>
      </el-col>
      <el-col :xs="24" :lg="9">
        <el-card shadow="never">
          <div slot="header">孤立关系</div>
          <div class="status-list">
            <div v-for="item in overview.orphanRelationList" :key="item.relation_name" class="status-item">
              <span>{{ item.relation_name }}</span>
              <strong>{{ item.orphan_count }}</strong>
            </div>
          </div>
        </el-card>
      </el-col>
    </el-row>

    <el-row :gutter="16" class="lower-row">
      <el-col :xs="24" :lg="10">
        <el-card shadow="never">
          <div slot="header">治理信号</div>
          <div class="status-list">
            <div v-for="item in overview.governanceSignalDistribution" :key="item.signal_name" class="status-item">
              <span>{{ item.signal_name }}</span>
              <strong>{{ item.signal_count || 0 }}</strong>
            </div>
          </div>
        </el-card>
      </el-col>
      <el-col :xs="24" :lg="14">
        <el-card shadow="never">
          <div slot="header">主数据变更趋势</div>
          <div class="trend-list">
            <div v-for="item in overview.monthlyDataChangeTrend" :key="item.month_label" class="trend-item">
              <span>{{ item.month_label }}</span>
              <strong>{{ item.staff_updates || 0 }}</strong>
              <em>文件 {{ item.doc_uploads || 0 }} / 角色 {{ item.role_changes || 0 }}</em>
            </div>
          </div>
        </el-card>
      </el-col>
    </el-row>

    <el-card class="lower-row" shadow="never">
      <div slot="header">重复工号</div>
      <el-table :data="overview.duplicateIdentityList" size="small" v-loading="loading" empty-text="暂无重复工号">
        <el-table-column prop="code" label="工号" width="140" />
        <el-table-column prop="duplicate_count" label="重复数" width="90" />
        <el-table-column prop="staff_names" label="员工" min-width="240" />
      </el-table>
    </el-card>
  </div>
</template>

<script>
import { getMasterDataGovernanceOverview } from '../api/masterDataGovernance'

export default {
  name: 'MasterDataGovernanceOverview',
  data () {
    return {
      loading: false,
      overview: {
        staffCount: 0,
        activeStaffCount: 0,
        deptCount: 0,
        criticalIssueCount: 0,
        avgQualityScore: 0,
        fieldGapDistribution: [],
        deptQualityRanking: [],
        highRiskMasterDataList: [],
        duplicateIdentityList: [],
        orphanRelationList: [],
        governanceSignalDistribution: [],
        monthlyDataChangeTrend: []
      }
    }
  },
  computed: {
    metrics () {
      return [
        { label: '员工主数据', value: this.overview.staffCount, meta: `${this.overview.activeStaffCount || 0} 人在职` },
        { label: '部门主数据', value: this.overview.deptCount, meta: '组织维度覆盖', tone: 'blue' },
        { label: '严重问题', value: this.overview.criticalIssueCount, meta: '低分或多缺口', tone: 'red' },
        { label: '平均质量分', value: this.overview.avgQualityScore || 0, meta: '跨模块评分', tone: 'orange' }
      ]
    }
  },
  mounted () {
    this.loadData()
  },
  methods: {
    loadData () {
      this.loading = true
      getMasterDataGovernanceOverview().then(response => {
        if (response.code === 200) {
          this.overview = Object.assign({}, this.overview, response.data || {})
        } else {
          this.$message.warning(response.message || '主数据质量治理中心加载失败')
        }
      }).finally(() => {
        this.loading = false
      })
    }
  }
}
</script>

<style lang="less" scoped>
.master-data-addon {
  padding: 20px;
  background: #f5f7fb;
  min-height: 100%;
}

.addon-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 18px;
  padding: 22px 24px;
  background: #ffffff;
  border: 1px solid #e6ebf2;
  border-radius: 6px;
}

.addon-head span {
  display: block;
  margin-bottom: 8px;
  color: #075985;
  font-size: 12px;
  font-weight: 700;
}

.addon-head h2 {
  margin: 0 0 8px;
  color: #17233d;
  font-size: 24px;
}

.addon-head p {
  margin: 0;
  color: #5f6f89;
}

.metric-row {
  margin-bottom: 16px;
}

.metric-card {
  min-height: 118px;
  padding: 18px;
  margin-bottom: 16px;
  background: #ffffff;
  border: 1px solid #e6ebf2;
  border-radius: 6px;
}

.metric-card span,
.metric-card em {
  display: block;
  color: #6b7a90;
  font-style: normal;
}

.metric-card strong {
  display: block;
  margin: 10px 0 8px;
  color: #17233d;
  font-size: 30px;
  line-height: 1;
}

.metric-card.red strong {
  color: #d93026;
}

.metric-card.orange strong {
  color: #d9822b;
}

.metric-card.blue strong {
  color: #2f6fec;
}

.lower-row {
  margin-top: 16px;
}

.status-list,
.trend-list {
  display: grid;
  gap: 10px;
}

.trend-list {
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
}

.status-item,
.trend-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 14px;
  background: #f8fafc;
  border: 1px solid #edf1f7;
  border-radius: 6px;
}

.trend-item {
  align-items: flex-start;
  flex-direction: column;
}

.status-item span,
.trend-item span,
.trend-item em {
  color: #5f6f89;
  font-style: normal;
}

.status-item strong,
.trend-item strong {
  color: #17233d;
  font-size: 16px;
}
</style>
